// Phase 0085-B — Assessment Cycles page.

import { useState } from 'react'
import { App, Button, DatePicker, Form, Input, Modal, Select, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useMutation, useQuery } from '@tanstack/react-query'

import { assessmentApi, type AssessmentCycle } from '@/features/hr/assessmentApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'default', OPEN: 'green', CLOSED: 'blue', LOCKED: 'gold', CANCELLED: 'red',
}

export default function AssessmentCyclesPage() {
  const { message } = App.useApp()
  const [createOpen, setCreateOpen] = useState(false)
  const [form] = Form.useForm()

  const { data: cycles = [], isLoading, refetch } = useQuery({
    queryKey: ['assessment-cycles'],
    queryFn: () => assessmentApi.listCycles(),
  })

  const { data: templates = [] } = useQuery({
    queryKey: ['assessment-templates'],
    queryFn: () => assessmentApi.listTemplates(),
  })

  const createMut = useMutation({
    mutationFn: (values: Partial<AssessmentCycle>) => assessmentApi.createCycle(values),
    onSuccess: () => { message.success('Cycle created'); setCreateOpen(false); form.resetFields(); refetch() },
    onError: (e: Error) => message.error(e.message),
  })

  const launchMut = useMutation({
    mutationFn: (id: string) => assessmentApi.launchCycle(id),
    onSuccess: (data: unknown) => {
      const d = data as { assessments_created: number; employees_targeted: number }
      message.success(`Launched: ${d.assessments_created} assessments created (${d.employees_targeted} employees)`)
      refetch()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const columns: ColumnsType<AssessmentCycle> = [
    { title: 'Code', dataIndex: 'cycle_code', width: 220, render: (c: string) => <Text code>{c}</Text> },
    { title: 'Name', dataIndex: 'cycle_name' },
    { title: 'Period', key: 'period', width: 220,
      render: (_, r) => `${dayjs(r.period_start).format('MMM D')} — ${dayjs(r.period_end).format('MMM D, YYYY')}` },
    { title: 'Status', dataIndex: 'status', width: 110,
      render: (s: string) => <Tag color={STATUS_COLOR[s]}>{s}</Tag> },
    {
      title: 'Action',
      key: 'action',
      width: 160,
      render: (_, r) => (
        <Space size={4}>
          {r.status === 'DRAFT' && (
            <Button size="small" type="primary"
                    onClick={() => launchMut.mutate(r.id)}
                    loading={launchMut.isPending}>
              Launch
            </Button>
          )}
          {r.status === 'OPEN' && <Tag color="green">Open</Tag>}
        </Space>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Assessment Cycles"
        subtitle="Quarterly / yearly review windows. Launching a cycle creates one assessment per active employee."
        extra={<Button type="primary" onClick={() => setCreateOpen(true)}>New Cycle</Button>}
      />
      <Table rowKey="id" columns={columns} dataSource={cycles}
             loading={isLoading} pagination={false} size="middle" />

      <Modal
        open={createOpen}
        title="Create Cycle"
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
      >
        <Form form={form} layout="vertical"
              onFinish={(v) => createMut.mutate({
                cycle_code: v.cycle_code,
                cycle_name: v.cycle_name,
                template_id: v.template_id,
                period_start: dayjs(v.period[0]).format('YYYY-MM-DD'),
                period_end: dayjs(v.period[1]).format('YYYY-MM-DD'),
              })}>
          <Form.Item name="cycle_code" label="Cycle Code" rules={[{ required: true }]}>
            <Input placeholder="e.g. ANNUAL_2026" />
          </Form.Item>
          <Form.Item name="cycle_name" label="Cycle Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="template_id" label="Template" rules={[{ required: true }]}>
            <Select options={templates.map((t) => ({ value: t.id, label: t.template_name }))} />
          </Form.Item>
          <Form.Item name="period" label="Period" rules={[{ required: true }]}>
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
